import { makeAuthedRequest } from "utils/makeAuthedRequest";
import { useQuery, useMutation, useQueryClient } from "react-query";

export function useAddresses({ fetchAddresses = true } = {}) {
  const queryClient = useQueryClient();

  const { data, status, error, isLoading, refetch } = useQuery(
    ["addresses"],
    () =>
      makeAuthedRequest("v1/settings/addresses", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      enabled: fetchAddresses,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const addResolveAddress = useMutation((resolveAddressData) =>
    makeAuthedRequest(`v1/settings/addresses/resolve`, {
      method: "POST",
      body: JSON.stringify(resolveAddressData),
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const addAddress = useMutation(
    (addressData) =>
      makeAuthedRequest(`v1/settings/addresses`, {
        method: "POST",
        body: JSON.stringify(addressData),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("addresses");
        queryClient.invalidateQueries("issues");
      },
    }
  );

  const updateAddress = useMutation(
    ({ iboAddressId, updateData }) =>
      makeAuthedRequest(`v1/settings/addresses/${iboAddressId}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.error);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("addresses");
      },
      onError: (err) => {
        console.error(err);
      },
    }
  );

  const deleteAddress = useMutation(
    (iboAddressId) =>
      makeAuthedRequest(`v1/settings/addresses/${iboAddressId}`, {
        method: "DELETE",
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.error);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("addresses");
      },
      onError: (err) => {
        console.error(err);
      },
    }
  );

  return {
    data,
    status,
    isLoading,
    error,
    refetch,
    addResolveAddress,
    addAddress,
    updateAddress,
    deleteAddress,
  };
}
